import { useDebounceEffect } from 'ahooks';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Invitation } from 'src/interfaces/invitation.interface';
import { getHttpStatus } from 'src/utils/requestUtils';

export const ChangeOrganization = () => {
  const { get, post } = useAuthedRequest();
  const [loading, setLoading] = useState(true);
  const notification = useNotification();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const orgIdParam = searchParams.get('organizationId');

  const acceptInvitation = async (organizationId: string) => {
    try {
      await post(`/invitations/accept`, { organizationId });
      navigate(`/org-switch?orgId=${organizationId}&returnTo=/settings`);
    } catch (error) {
      const status = getHttpStatus(error);
      if (status === 404) {
        notification.error('Invite not found or expired');
      } else if (status === 403) {
        notification.error('Plan limit reached');
      } else {
        notification.error('Failed to accept invitation');
      }
      console.error('Error updating organization:', error);
      navigate('/settings');
    }
  };

  useDebounceEffect(() => {
    if (!orgIdParam) {
      navigate('/');
      return;
    }

    setOrganizationId((prev) => (prev === orgIdParam ? prev : orgIdParam));
    setShowConfirmModal(true);
  }, [orgIdParam]);

  useEffect(() => {
    if (organizationId) {
      const fetchInvitations = async () => {
        setLoading(true);
        const invitations = await get(`/invitations`);
        const filteredInvitations = invitations.filter(
          (invitation: Invitation) =>
            invitation.organization?.id === organizationId,
        );
        if (filteredInvitations.length === 0) {
          notification.error(
            `No invitations found for organization: ${organizationId}. Please contact your administrator.`,
          );
          navigate('/');
          return;
        }
        setOrganizationName(filteredInvitations[0].organization?.name);
        setLoading(false);
      };
      fetchInvitations();
    }
  }, [organizationId, get]);

  const handleConfirm = () => {
    if (organizationId) {
      acceptInvitation(organizationId);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className="flex-1">
      {loading && (
        <ContentWrapper>
          <Loading />
        </ContentWrapper>
      )}

      <Modal
        title="Accept Organization Invitation"
        isOpen={showConfirmModal}
        onClose={handleCancel}
        actionButton={
          <Button onClick={handleConfirm}>Accept Invitation</Button>
        }
        className="lg:max-w-[600px] md:max-w-[500px]"
      >
        <div className="space-y-4">
          <p>
            You've been invited to join <b>{organizationName}</b> org!
          </p>
          <p>
            Would you like to accept this invitation to{' '}
            <b>{organizationName}</b>?
          </p>
        </div>
      </Modal>
    </div>
  );
};
